import { defineLiveHandler, resourceListTag, resourceTag, type LiveEnvelope, type LiveHandler } from '@krp/client-runtime'

import { AQSI_TAGS } from '../../../shared/api/tags'

/** Типы событий службы AQSI о кассах (договор с `AqsiLiveEvents` службы). */
export const AQSI_LIVE_EVENTS = {
  registerChanged: 'aqsi.register.changed',
  deviceChanged: 'aqsi.device.changed',
  operationChanged: 'aqsi.operation.changed',
} as const

/** Номер кассы в событии: служба шлёт `deviceId` числом (работа по кассе кабинета, RP-C9). */
const deviceOf = (envelope: LiveEnvelope): string | undefined => {
  const value = envelope.payload?.deviceId
  if (typeof value === 'number' && Number.isInteger(value)) return String(value)
  return typeof value === 'string' && value !== '' ? value : undefined
}

/** Кассы ресурса — то, что видит экран РМК и список «Новая смена». */
const seatTags = (resourceId: string) => [resourceListTag(AQSI_TAGS.registers, resourceId)]

/**
 * События касс (LIVE): кто-то сел за кассу, вышел, забрал её, открыл или
 * закрыл смену, проверил связь с аппаратом — открытые окна этого ресурса
 * перечитывают кассы сами, без перезагрузки. Смена кассы — дорогое чтение
 * кабинета: перечитывается только у названной кассы и только если её окно
 * открыто (RTK перечитывает лишь подписанные запросы), и не при переподключении.
 */
const textOf = (envelope: LiveEnvelope, key: string): string | undefined => {
  const value = envelope.payload?.[key]
  return typeof value === 'string' && value !== '' ? value : undefined
}

export const rmkLiveHandlers: readonly LiveHandler[] = [
  // Кабинет сообщил вебхуком о смене статуса операции, служба её перечитала: окно перечитывает именно её, продажу и смену — без опроса.
  defineLiveHandler({
    type: AQSI_LIVE_EVENTS.operationChanged,
    invalidates: (envelope) => {
      if (envelope.resourceId === null) return []
      const operationId = textOf(envelope, 'operationId')
      const saleId = textOf(envelope, 'saleId')
      const deviceId = deviceOf(envelope)
      return [
        ...(operationId !== undefined ? [resourceTag(AQSI_TAGS.operation, envelope.resourceId, operationId)] : []),
        ...(saleId !== undefined
          ? [resourceTag(AQSI_TAGS.sale, envelope.resourceId, saleId), resourceTag(AQSI_TAGS.saleFiscal, envelope.resourceId, saleId)]
          : []),
        resourceListTag(AQSI_TAGS.saleActive, envelope.resourceId),
        ...(deviceId !== undefined ? [resourceTag(AQSI_TAGS.shift, envelope.resourceId, deviceId)] : []),
        ...seatTags(envelope.resourceId),
      ]
    },
    resync: [AQSI_TAGS.saleActive, AQSI_TAGS.operation, AQSI_TAGS.registers],
  }),
  defineLiveHandler({
    type: AQSI_LIVE_EVENTS.registerChanged,
    invalidates: (envelope) => {
      if (envelope.resourceId === null) return []
      const deviceId = deviceOf(envelope)
      return [
        ...seatTags(envelope.resourceId),
        ...(deviceId !== undefined
          ? [
              resourceTag(AQSI_TAGS.shift, envelope.resourceId, deviceId),
              resourceTag(AQSI_TAGS.journal, envelope.resourceId, deviceId),
              resourceTag(AQSI_TAGS.link, envelope.resourceId, deviceId),
            ]
          : []),
      ]
    },
    resync: [AQSI_TAGS.registers],
  }),
  defineLiveHandler({
    type: AQSI_LIVE_EVENTS.deviceChanged,
    invalidates: (envelope) => {
      if (envelope.resourceId === null) return []
      const deviceId = deviceOf(envelope)
      return [
        ...seatTags(envelope.resourceId),
        resourceListTag(AQSI_TAGS.devices, envelope.resourceId),
        ...(deviceId !== undefined ? [resourceTag(AQSI_TAGS.link, envelope.resourceId, deviceId)] : []),
      ]
    },
    resync: [AQSI_TAGS.registers, AQSI_TAGS.devices, AQSI_TAGS.link],
  }),
]
